import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../redux/main/authSlice";
import { selectAccountData } from "../redux/main/accountSlice";
import {
    getProductSettings,
    createProductSetting,
    addSettingValue,
    removeSettingValue,
    selectProductSettings,
    selectProductSettingsLoading,
    selectProductSettingsCreateLoading,
    selectProductSettingsError
} from "../redux/productSettings/productSettingsSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";

const defaultPurchaseUnits = [
    { label: "Pezzo", active: true },
    { label: "Kg", active: true },
    { label: "Metro", active: true },
    { label: "Litro", active: true },
    { label: "Scatola", active: true },
    { label: "Pallet", active: true }
];

const defaultVatRates = [
    { label: "IVA 22%", value: "22", active: true },
    { label: "IVA 10%", value: "10", active: true },
    { label: "IVA 4%", value: "4", active: true },
    { label: "Esente IVA", value: "0", active: true }
];

export default function ProductSettingsList() {
    const dispatch = useDispatch();
    const authUser = useSelector(selectUser);
    const accountData = useSelector(selectAccountData);
    const settings = useSelector(selectProductSettings);
    const loading = useSelector(selectProductSettingsLoading);
    const createLoading = useSelector(selectProductSettingsCreateLoading);
    const token = localStorage.getItem("installamelo-auth-token") ?? "";

    const settingsError = useSelector(selectProductSettingsError);
    const [newValues, setNewValues] = useState({});
    const [newVatValues, setNewVatValues] = useState({});
    const [localError, setLocalError] = useState(null);

    const isAdmin = authUser && (authUser.admin === true || authUser.roleLabel === 'admin' || authUser.typeLabel === 'admin');

    useEffect(() => {
        if (accountData?._id) {
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    }, [accountData, dispatch, token]);

    const hasPurchaseUnits = settings?.some(s => s.key === "purchaseUnits");
    const hasVatRates = settings?.some(s => s.key === "vatRates");

    const handleInitializePurchaseUnits = async () => {
        setLocalError(null);
        if (!accountData?._id) {
            setLocalError('Account non caricato. Ricaricare la pagina.');
            return;
        }
        if (!token) {
            setLocalError('Sessione scaduta. Effettuare nuovamente il login.');
            return;
        }
        const setting = {
            accountId: accountData._id,
            key: "purchaseUnits",
            label: "Unita' di Acquisto",
            values: defaultPurchaseUnits
        };
        const result = await dispatch(createProductSetting({ setting, token }));
        if (result.error) {
            setLocalError('Errore inizializzazione unità: ' + (result.payload || result.error.message));
        } else {
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    };

    const handleInitializeVatRates = async () => {
        setLocalError(null);
        if (!accountData?._id) {
            setLocalError('Account non caricato. Ricaricare la pagina.');
            return;
        }
        if (!token) {
            setLocalError('Sessione scaduta. Effettuare nuovamente il login.');
            return;
        }
        const setting = {
            accountId: accountData._id,
            key: "vatRates",
            label: "Aliquote IVA",
            values: defaultVatRates
        };
        const result = await dispatch(createProductSetting({ setting, token }));
        if (result.error) {
            setLocalError('Errore inizializzazione IVA: ' + (result.payload || result.error.message));
        } else {
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    };

    const handleAddValue = async (settingId) => {
        const label = (newValues[settingId] || "").trim();
        if (!label) return;
        const result = await dispatch(addSettingValue({
            settingId,
            value: { label, active: true },
            token
        }));
        if (!result.error) {
            setNewValues(prev => ({ ...prev, [settingId]: "" }));
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    };

    const handleAddVatValue = async (settingId) => {
        const label = (newVatValues[settingId + '_label'] || "").trim();
        const value = (newVatValues[settingId + '_value'] || "").trim();
        if (!label) return;
        const result = await dispatch(addSettingValue({
            settingId,
            value: { label, value, active: true },
            token
        }));
        if (!result.error) {
            setNewVatValues(prev => ({ ...prev, [settingId + '_label']: "", [settingId + '_value']: "" }));
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    };

    const handleRemoveValue = async (settingId, valueId) => {
        const result = await dispatch(removeSettingValue({ settingId, valueId, token }));
        if (!result.error) {
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    Accesso non autorizzato. Solo gli amministratori possono accedere a questa pagina.
                </div>
            </div>
        );
    }

    if (loading) {
        return <LoadingSection />;
    }

    // Separiamo le impostazioni per tipo
    const purchaseUnitsSetting = settings?.find(s => s.key === "purchaseUnits");
    const vatRatesSetting = settings?.find(s => s.key === "vatRates");
    const otherSettings = settings?.filter(s => s.key !== "purchaseUnits" && s.key !== "vatRates") || [];

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Impostazioni Prodotti</h1>
            </div>

            {/* Errori visibili */}
            {(localError || settingsError) && (
                <div className="mb-4 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded flex items-start gap-2">
                    <DynamicIcon iconClass="fa-solid fa-circle-exclamation mt-0.5" />
                    <div>
                        <strong>Errore:</strong> {localError || settingsError}
                    </div>
                    <button className="ml-auto text-red-400 hover:text-red-600" onClick={() => setLocalError(null)}>
                        <DynamicIcon iconClass="fa-solid fa-times" />
                    </button>
                </div>
            )}

            {/* Info diagnostica (temporanea) */}
            {!accountData?._id && (
                <div className="mb-4 bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded text-sm">
                    <DynamicIcon iconClass="fa-solid fa-triangle-exclamation mr-2" />
                    Account non ancora caricato — ricaricare la pagina se i pulsanti non rispondono.
                </div>
            )}

            <div className="space-y-6">

                {/* SEZIONE: Unita' di Acquisto */}
                <div className="bg-white shadow-md rounded-lg p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-ruler text-blue-500" />
                        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                            Unita' di Acquisto
                        </span>
                    </div>

                    {!purchaseUnitsSetting ? (
                        <div className="text-center py-4">
                            <p className="text-gray-500 mb-3 text-sm">Nessuna unita' di acquisto configurata.</p>
                            <button
                                onClick={handleInitializePurchaseUnits}
                                disabled={createLoading}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
                            >
                                {createLoading ? (
                                    <><DynamicIcon iconClass="fa-solid fa-spinner fa-spin mr-2" /> Inizializzazione...</>
                                ) : (
                                    <><DynamicIcon iconClass="fa-solid fa-cog mr-2" /> Inizializza Unita'</>
                                )}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {purchaseUnitsSetting.values && purchaseUnitsSetting.values.map((val) => (
                                    <span
                                        key={val._id}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                                    >
                                        {val.label}
                                        <button
                                            onClick={() => handleRemoveValue(purchaseUnitsSetting._id, val._id)}
                                            className="ml-2 text-gray-400 hover:text-red-500 focus:outline-none"
                                            title="Rimuovi"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-times text-xs" />
                                        </button>
                                    </span>
                                ))}
                                {(!purchaseUnitsSetting.values || purchaseUnitsSetting.values.length === 0) && (
                                    <span className="text-sm text-gray-400">Nessun valore configurato</span>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newValues[purchaseUnitsSetting._id] || ""}
                                    onChange={(e) => setNewValues(prev => ({ ...prev, [purchaseUnitsSetting._id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddValue(purchaseUnitsSetting._id);
                                    }}
                                    placeholder="Nuova unita'..."
                                    className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                <button
                                    onClick={() => handleAddValue(purchaseUnitsSetting._id)}
                                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                >
                                    <DynamicIcon iconClass="fa-solid fa-plus mr-1" />
                                    Aggiungi
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* SEZIONE: Aliquote IVA */}
                <div className="bg-white shadow-md rounded-lg p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-percent text-green-500" />
                        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                            Aliquote IVA
                        </span>
                    </div>

                    {!vatRatesSetting ? (
                        <div className="text-center py-4">
                            <p className="text-gray-500 mb-3 text-sm">Nessuna aliquota IVA configurata.</p>
                            <button
                                onClick={handleInitializeVatRates}
                                disabled={createLoading}
                                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50"
                            >
                                {createLoading ? (
                                    <><DynamicIcon iconClass="fa-solid fa-spinner fa-spin mr-2" /> Inizializzazione...</>
                                ) : (
                                    <><DynamicIcon iconClass="fa-solid fa-cog mr-2" /> Inizializza Aliquote IVA</>
                                )}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {vatRatesSetting.values && vatRatesSetting.values.map((val) => (
                                    <span
                                        key={val._id}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                                    >
                                        {val.label}
                                        {val.value && <span className="ml-1 text-green-600">({val.value}%)</span>}
                                        <button
                                            onClick={() => handleRemoveValue(vatRatesSetting._id, val._id)}
                                            className="ml-2 text-green-400 hover:text-red-500 focus:outline-none"
                                            title="Rimuovi"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-times text-xs" />
                                        </button>
                                    </span>
                                ))}
                                {(!vatRatesSetting.values || vatRatesSetting.values.length === 0) && (
                                    <span className="text-sm text-gray-400">Nessun valore configurato</span>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newVatValues[vatRatesSetting._id + '_label'] || ""}
                                    onChange={(e) => setNewVatValues(prev => ({ ...prev, [vatRatesSetting._id + '_label']: e.target.value }))}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddVatValue(vatRatesSetting._id);
                                    }}
                                    placeholder="Etichetta (es. IVA 22%)"
                                    className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                <input
                                    type="text"
                                    value={newVatValues[vatRatesSetting._id + '_value'] || ""}
                                    onChange={(e) => setNewVatValues(prev => ({ ...prev, [vatRatesSetting._id + '_value']: e.target.value }))}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddVatValue(vatRatesSetting._id);
                                    }}
                                    placeholder="Valore % (es. 22)"
                                    className="w-32 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                <button
                                    onClick={() => handleAddVatValue(vatRatesSetting._id)}
                                    className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                                >
                                    <DynamicIcon iconClass="fa-solid fa-plus mr-1" />
                                    Aggiungi
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* SEZIONE: Altre impostazioni */}
                {otherSettings.map((setting) => (
                    <div key={setting._id} className="bg-white shadow-md rounded-lg p-4">
                        <div className="mb-3">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                {setting.label || setting.key}
                            </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4">
                            {setting.values && setting.values.map((val) => (
                                <span
                                    key={val._id}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                                >
                                    {val.label}
                                    {val.value && <span className="ml-1 text-gray-500">({val.value})</span>}
                                    <button
                                        onClick={() => handleRemoveValue(setting._id, val._id)}
                                        className="ml-2 text-gray-400 hover:text-red-500 focus:outline-none"
                                        title="Rimuovi"
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-times text-xs" />
                                    </button>
                                </span>
                            ))}
                            {(!setting.values || setting.values.length === 0) && (
                                <span className="text-sm text-gray-400">Nessun valore configurato</span>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newValues[setting._id] || ""}
                                onChange={(e) => setNewValues(prev => ({ ...prev, [setting._id]: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAddValue(setting._id);
                                }}
                                placeholder="Nuovo valore..."
                                className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <button
                                onClick={() => handleAddValue(setting._id)}
                                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                            >
                                <DynamicIcon iconClass="fa-solid fa-plus mr-1" />
                                Aggiungi
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
